import { useCallback, useEffect, useRef } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Asset, Box, Button, Card, Svg, Typography } from '@altermaind/web/ui';
import { useNotification } from '@/_shared/hooks';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDownloadEmployeesImportsTemplateCall } from '../../state/api';

export const UsefulInformation = () => {
  const t = useTranslations('people.usefullInformations');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { genericResponsiveValue } = useResponsiveStyle();
  const { openDialog: openDialogPeopleTutorial } = useDialog('peopleTutorial');
  const box1 = useRef(null);
  const box2 = useRef(null);

  const {
    loading: loadingGetDownloadEmployeesImportsTemplate,
    execute: executeGetDownloadEmployeesImportsTemplate,
  } = getDownloadEmployeesImportsTemplateCall.hook();

  const handleDownload = useCallback(() => {
    executeGetDownloadEmployeesImportsTemplate({
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
  }, [executeGetDownloadEmployeesImportsTemplate, notify, tShared]);

  const openTutorial = () => {
    openDialogPeopleTutorial();
  };

  useEffect(() => {
    if (box1.current && box2.current) {
      const heightBox1 = (box1.current as HTMLDivElement).offsetHeight;
      const heightBox2 = (box2.current as HTMLDivElement).offsetHeight;
      if (heightBox1 > heightBox2) {
        (box2.current as HTMLDivElement).style.height = `${heightBox1}px`;
      }
      if (heightBox2 > heightBox1) {
        (box1.current as HTMLDivElement).style.height = `${heightBox2}px`;
      }
    }
  }, [box1, box2]);

  return (
    <Box>
      <Typography variant='h3' mt='l' mb='s'>
        {t('title')}
      </Typography>
      <Box
        display='grid'
        gridTemplateColumns={genericResponsiveValue({
          breakPoints: { desktop: 'repeat(2, 1fr)' },
          defaultValue: 'repeat(1, 1fr)',
        })}
      >
        <Box
          ref={box1}
          mr={genericResponsiveValue({
            breakPoints: { desktop: 'xs' },
            defaultValue: 'none',
          })}
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'xs',
          })}
        >
          <Card p='xs'>
            <Box
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              display='flex'
            >
              <Box>
                <Asset
                  width={genericResponsiveValue({
                    breakPoints: { desktop: 'auto' },
                    defaultValue: '100%',
                  })}
                  borderRadius='m'
                  domain='images'
                  name='peopleTutorialImage'
                  height='170px'
                />
              </Box>
              <Box
                alignSelf={genericResponsiveValue({
                  breakPoints: { desktop: 'center' },
                  defaultValue: 'flex-start',
                })}
                px={genericResponsiveValue({
                  breakPoints: { desktop: 'xs' },
                  defaultValue: 'none',
                })}
              >
                <Typography variant='h2/light' fontSize='s'>
                  {t('boxTutorial.title')}
                </Typography>
                <Typography variant='p2/light' fontSize='xs'>
                  {t('boxTutorial.description')}
                </Typography>
                <Button onClick={openTutorial} variant='positive' mt='xs' p='xxs' height='m'>
                  {t('boxTutorial.buttonLabel')}
                </Button>
              </Box>
            </Box>
          </Card>
        </Box>
        <Box ref={box2}>
          <Card
            width='100%'
            borderColor='grey/dark'
            border='1px solid'
            backgroundColor='transparent'
            display='flex'
            flexDirection='column'
            justifyContent='space-between'
            height='100%'
          >
            <Box alignContent='flex-start'>
              <Typography variant='h4' mb='xxs'>
                {t('boxTemplate.title')}
              </Typography>
              <Typography variant='p2/light' fontSize='xs'>
                {t('boxTemplate.description')}
              </Typography>
            </Box>
            <Box alignContent='flex-end'>
              <Button
                onClick={handleDownload}
                loading={loadingGetDownloadEmployeesImportsTemplate}
                disabled={loadingGetDownloadEmployeesImportsTemplate}
                variant='link/primary'
                mb='none'
                height='auto'
                mt='xs'
              >
                <Svg name='download' size='s' />{' '}
                <Typography ml='xxs' as='span'>
                  {t('boxTemplate.buttonLabel')}
                </Typography>
              </Button>
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
